<template>
  <slot v-if="isAuthenticated"></slot>
  <Loading v-if="isLoading" message="Verifying user session..." />
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { IApp } from "@/types";
import { URLs } from "@/constants";
import _ from "lodash";
import Loading from "../Widgets/Loading.vue";
import { useCustomerStore } from "@/stores/customer";
import { useProfileStore } from "@/stores/profile";
import { useReportStore } from "@/stores/report";
import Auth from "@/utils/auth";

const router = useRouter();
const authStore = useAuthStore();
const customerStore = useCustomerStore();
const profileStore = useProfileStore();
const reportStore = useReportStore();

const isAuthenticated = computed(() => authStore.isAuthenticated);
const authLoadingStatus = computed(() => authStore.status);
const profileLoadingStatus = computed(() => profileStore.profileLoadingStatus);
const promptLoadingStatus = computed(() => reportStore.promptLoadingStatus);
const user = computed(() => authStore.user);

const isLoading = ref(false);

const fetchPromptIfAllowed = async () => {
  if (
    promptLoadingStatus.value === IApp.ILoadingStatus.Idle &&
    authStore.hasPermission(Auth.Permission.REPORT_PROMPT_GET)
  ) {
    await reportStore.fetchPrompt();
  }
};

onMounted(async () => {
  isLoading.value = true;
  if (profileLoadingStatus.value === IApp.ILoadingStatus.Idle) {
    await profileStore.fetch();
  }
  if (authLoadingStatus.value === IApp.ILoadingStatus.Idle) {
    const authResponse = await authStore.getUserInfo();
    if (authResponse.status && authStore.hasPermission(Auth.Permission.CUSTOMER_LIST)) {
      await customerStore.fetchCustomers(user.value?.id!);
    }
    if (authResponse.status) {
      await fetchPromptIfAllowed();
    }
  } else if (
    _.includes([IApp.ILoadingStatus.Success, IApp.ILoadingStatus.Error], authLoadingStatus.value) &&
    !user.value
  ) {
    router.push(URLs.PAGE_AUTH_LOGIN);
  } else {
    await fetchPromptIfAllowed();
  }
  isLoading.value = false;
});

watch([authLoadingStatus, user], ([newStatus, newUser]) => {
  if (
    newStatus === IApp.ILoadingStatus.Error ||
    (newStatus === IApp.ILoadingStatus.Success && !newUser)
  ) {
    router.push(URLs.PAGE_AUTH_LOGIN);
  }
});
</script>
